import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardCopy, Loader2, PackageCheck, X } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import {
  AREA, IMPACT, KIND, PRIORITY, PRIORITY_ORDER, STATUS, STATUS_ORDER, deliveryLine, eventLine, reporterWhere,
  statusNeeds, statusNotePrompt, ticketLabel,
} from "../../../lib/tickets";
import { Pill } from "./TicketsTab";
import useDevCopy from "./useDevCopy";
import DeliverDialog from "./DeliverDialog";
import TicketTime from "./TicketTime";
import TicketScreenshots from "./TicketScreenshots";
import TicketDiscussion from "./TicketDiscussion";

/** A moment as the team reads it: local time, to the minute. */
function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
}

/**
 * One ticket (14.10.2026): what was reported and its history, for whoever may
 * read it; and for the owner alone, the state, the priority and the time.
 */
export default function TicketDrawer({ ticketId, isOwner = false, readsAll = false, me = "", onChanged, onClose }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shots, setShots] = useState([]);
  const [shotsLoading, setShotsLoading] = useState(true);
  const [copyForDevelopment, copyFallback] = useDevCopy();
  // The parent's handlers change on each of its renders; reading them through
  // refs keeps `reload` tied to the ticket alone, so a refreshed list behind
  // the drawer does not load the ticket again, and again.
  const closeRef = useRef(onClose);
  const changedRef = useRef(onChanged);
  closeRef.current = onClose;
  changedRef.current = onChanged;

  const reload = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get(`/admin/tickets/${ticketId}`);
      setTicket(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Tichetul nu a putut fi încărcat."));
      closeRef.current?.();
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // The screenshots, with links that last minutes: asked for with the ticket.
  const loadShots = useCallback(async () => {
    try {
      const r = await api.get(`/admin/tickets/${ticketId}/attachments`);
      setShots(r.data.items || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Capturile nu au putut fi încărcate."));
    } finally {
      setShotsLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { loadShots(); }, [loadShots]);

  // Escape closes the drawer, as it closes the dialogs.
  useEffect(() => {
    const onKey = (e) => {
      // A dialog open over the drawer takes its own Escape.
      if (e.key !== "Escape" || document.querySelector('[role="dialog"][data-state="open"]')) return;
      closeRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Opened by the owner, a ticket is no longer unread: the list and the badge follow.
  useEffect(() => {
    reload().then(() => { if (isOwner) changedRef.current?.(); });
  }, [reload, isOwner]);

  const applied = (fresh, message) => {
    setTicket(fresh);
    toast.success(message);
    onChanged?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose} data-testid="ticket-drawer">
      <div className="bg-[#F5F6F6] w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Inside the panel: a dialog's clicks bubble through React to here, not to the backdrop. */}
        {copyFallback}
        <header className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold inline-flex items-center gap-2">
              <span className="font-mono text-[#7A4986]">{ticket ? ticketLabel(ticket) : "Tichet"}</span>
              {ticket && <Pill meta={STATUS[ticket.status]} />}
            </div>
            <h3 className="font-display text-lg font-bold text-[#373A45] mt-0.5" style={{ textWrap: "balance" }}>
              {loading ? "Se încarcă…" : ticket?.title}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {readsAll && ticket && (
              <button onClick={() => copyForDevelopment([ticket.id])}
                className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6] inline-flex items-center gap-1.5"
                data-testid="ticket-copy-dev" title="Copiază tichetul ca text, pentru conversația de dezvoltare">
                <ClipboardCopy size={12} /> <span className="hidden sm:inline">Copiază pentru dezvoltare</span>
              </button>
            )}
            <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide"><X size={20} /></button>
          </div>
        </header>

        {loading || !ticket ? (
          <div className="p-8 text-center text-[#6B7280]"><Loader2 className="animate-spin inline" /></div>
        ) : (
          <div className="p-6 space-y-5">
            {me && me === ticket.reporter?.email && ["to_verify", "resolved"].includes(ticket.status) && (
              <VerifyBox ticket={ticket} onDone={(fresh, message) => { reload(true); toast.success(message); changedRef.current?.(); }} />
            )}

            {isOwner && <OwnerControls ticket={ticket} onApplied={applied} onDelivered={() => { reload(true); changedRef.current?.(); }} />}

            <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Row label="Tip" value={<Pill meta={KIND[ticket.kind]} />} />
                <Row label="Cât îl încurcă" value={<span className={IMPACT[ticket.impact]?.cls}>{IMPACT[ticket.impact]?.label}</span>} />
                <Row label="Prioritate" value={ticket.priority ? <Pill meta={PRIORITY[ticket.priority]} /> : "nestabilită"} />
                <Row label="Zona" value={AREA[ticket.area] || "—"} />
                <Row label="Raportat de" value={[ticket.reporter?.name, ticket.reporter?.email, reporterWhere(ticket.reporter)]
                  .filter(Boolean).join(" · ")} />
                <Row label="Când" value={when(ticket.created_at)} />
              </div>
              {(ticket.links || []).length > 0 && (
                <ul className="mt-4 space-y-1 text-sm text-[#373A45]" data-testid="ticket-links">
                  {ticket.links.map((l, i) => (
                    <li key={`${l.at}-${i}`} className="flex items-center gap-2">
                      <PackageCheck size={14} className="text-[#519872]" />
                      {l.pr ? <a href={l.pr} target="_blank" rel="noopener noreferrer" className="underline">{deliveryLine(l)}</a> : deliveryLine(l)}
                      <span className="text-xs text-[#6B7280]">{when(l.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {ticket.status === "rejected" && ticket.reject_reason && (
                <p className="mt-4 text-sm text-[#373A45] bg-[#F5F6F6] rounded-lg px-3 py-2">
                  <strong>Respins:</strong> {ticket.reject_reason}
                </p>
              )}
              {ticket.status === "duplicate" && ticket.duplicate_of && (
                <p className="mt-4 text-sm text-[#373A45] bg-[#F5F6F6] rounded-lg px-3 py-2">
                  Dublură a tichetului <strong className="font-mono">T-{ticket.duplicate_of.number}</strong>.
                </p>
              )}
            </section>

            <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <Heading>{ticket.kind === "bug" ? "Ce s-a întâmplat" : "Ce ar vrea"}</Heading>
              <p className="[overflow-wrap:anywhere] text-sm text-[#373A45] whitespace-pre-wrap leading-relaxed" data-testid="ticket-description-text">{ticket.description}</p>
              {ticket.steps && (
                <>
                  <Heading className="mt-5">Pașii</Heading>
                  <p className="[overflow-wrap:anywhere] text-sm text-[#373A45] whitespace-pre-wrap leading-relaxed">{ticket.steps}</p>
                </>
              )}
            </section>

            <TicketDiscussion ticket={ticket} me={me} screenshots={shots}
              onSent={() => { reload(true); loadShots(); changedRef.current?.(); }} />

            <TicketScreenshots ticketId={ticket.id} items={shots} loading={shotsLoading} load={loadShots}
              isOwner={isOwner} onChanged={() => reload(true)} />

            <Context context={ticket.context} />

            {isOwner && <TicketTime ticketId={ticket.id} onChanged={() => { reload(true); onChanged?.(); }} />}

            <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <Heading>Istoric</Heading>
              <ol className="space-y-2 text-sm" data-testid="ticket-history">
                {[...(ticket.events || [])].reverse().map((ev, i) => (
                  <li key={`${ev.at}-${i}`} className="flex gap-3">
                    <span className="text-xs text-[#6B7280] whitespace-nowrap tabular-nums pt-0.5">{when(ev.at)}</span>
                    <span className="text-[#373A45]">
                      {eventLine(ev)}
                      {ev.actor && <span className="text-[#9CA3AF]"> · {ev.actor}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * For who reported the ticket, once a fix is live: does it work now? "Da"
 * closes it; "Tot nu merge" reopens it with what still fails. A resolved
 * ticket — even one closed by itself — can be reopened the same way.
 */
function VerifyBox({ ticket, onDone }) {
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const waiting = ticket.status === "to_verify";

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/tickets/${ticket.id}/confirm`);
      onDone(r.data, "Mulțumim! Tichetul e rezolvat.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut confirma."));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    if (!text.trim()) { toast.error("Scrie ce nu merge încă."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/tickets/${ticket.id}/reopen`, { text });
      onDone(r.data, "Tichetul a fost redeschis. Echipa a primit mesajul tău.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut redeschide tichetul."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`rounded-2xl border p-5 ${waiting ? "bg-[#519872]/[0.07] border-[#519872]/40" : "bg-white border-[#E5E7EB]"}`}
      data-testid="ticket-verify">
      {waiting ? (
        <>
          <h4 className="font-semibold text-[#2F6B4E]">Corectura e live. Merge acum?</h4>
          <p className="mt-1 text-sm text-[#373A45]">Dacă nu ne spui în 7 zile, considerăm că problema e rezolvată.</p>
        </>
      ) : (
        <p className="text-sm text-[#373A45]">Tichetul e rezolvat. Dacă problema a apărut din nou, îl poți redeschide.</p>
      )}
      {writing ? (
        <div className="mt-3 space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={5000} autoFocus
            placeholder="Ce nu merge încă? Pe ce pagină, la ce grupă…"
            className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg" data-testid="ticket-reopen-text" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setWriting(false)} disabled={busy}
              className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]">Renunță</button>
            <button onClick={reopen} disabled={busy}
              className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#9b1f2c] text-white hover:bg-[#7d1823] disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="ticket-reopen-send">
              {busy && <Loader2 size={12} className="animate-spin" />} Redeschide tichetul
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex gap-2 flex-wrap">
          {waiting && (
            <button onClick={confirm} disabled={busy}
              className="rounded-full px-4 py-2 text-xs font-semibold bg-[#519872] text-white hover:bg-[#3f7a5b] disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="ticket-confirm">
              {busy && <Loader2 size={12} className="animate-spin" />} Da, merge
            </button>
          )}
          <button onClick={() => setWriting(true)} disabled={busy}
            className="rounded-full px-4 py-2 text-xs font-semibold border border-[#9b1f2c]/40 text-[#9b1f2c] hover:bg-[#9b1f2c]/5"
            data-testid="ticket-still-broken">
            Tot nu merge
          </button>
        </div>
      )}
    </section>
  );
}

/** The owner's moves on a ticket: its state, its priority, its delivery. */
function OwnerControls({ ticket, onApplied, onDelivered }) {
  const [delivering, setDelivering] = useState(false);
  const [status, setStatus] = useState(ticket.status);
  const [reason, setReason] = useState("");
  const [original, setOriginal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(ticket.status); setReason(""); setOriginal(""); }, [ticket.status]);

  const needs = status !== ticket.status ? statusNeeds(status) : null;

  const saveStatus = async () => {
    if (status === ticket.status) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/tickets/${ticket.id}/status`, {
        status, reason, duplicate_of: needs === "duplicate_of" ? original : null,
      });
      onApplied(r.data, `${ticketLabel(ticket)}: ${STATUS[status].label}.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Starea nu a putut fi schimbată."));
    } finally {
      setBusy(false);
    }
  };

  const savePriority = async (priority) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/tickets/${ticket.id}/priority`, { priority: priority || null });
      onApplied(r.data, priority ? `Prioritate: ${PRIORITY[priority].label}.` : "Prioritatea a fost scoasă.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Prioritatea nu a putut fi schimbată."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#965EA7]/30 p-5" data-testid="ticket-owner-controls">
      <Heading>Ce faci cu tichetul</Heading>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-semibold text-[#373A45] mb-1">Stare</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy}
            className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg" data-testid="ticket-status-select">
            {STATUS_ORDER.map((key) => <option key={key} value={key}>{STATUS[key].label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-[#373A45] mb-1">Prioritate</span>
          <select value={ticket.priority || ""} onChange={(e) => savePriority(e.target.value)} disabled={busy}
            className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg" data-testid="ticket-priority-select">
            <option value="">Nestabilită</option>
            {PRIORITY_ORDER.map((key) => <option key={key} value={key}>{PRIORITY[key].label}</option>)}
          </select>
        </label>
      </div>

      {!["to_verify", "resolved", "rejected", "duplicate"].includes(ticket.status) && status === ticket.status && (
        <div className="mt-3">
          <button onClick={() => setDelivering(true)}
            className="text-xs font-semibold text-[#7A4986] hover:underline inline-flex items-center gap-1.5"
            data-testid="ticket-deliver-one">
            <PackageCheck size={13} /> Marchează livrat, cu eticheta și PR-ul
          </button>
          <DeliverDialog open={delivering} labels={[ticketLabel(ticket)]} onClose={() => setDelivering(false)} onDone={onDelivered} />
        </div>
      )}

      {status !== ticket.status && (
        <div className="mt-4 space-y-3">
          {needs === "duplicate_of" ? (
            <input value={original} onChange={(e) => setOriginal(e.target.value)} placeholder="Tichetul original, de exemplu T-12"
              className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg" data-testid="ticket-duplicate-of" />
          ) : (
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={statusNotePrompt(status)}
              className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg" data-testid="ticket-status-note" />
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setStatus(ticket.status)} disabled={busy}
              className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]">
              Renunță
            </button>
            <button onClick={saveStatus} disabled={busy}
              className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="ticket-status-save">
              {busy && <Loader2 size={12} className="animate-spin" />} Treci pe „{STATUS[status].label}”
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/** What the form noted by itself, for whoever looks into the problem. */
function Context({ context }) {
  const c = context || {};
  const rows = [
    ["Pagina", c.page], ["Browser", c.browser], ["Ecran", c.screen],
    ["Versiunea site-ului", c.site_version], ["Versiunea API", c.api_version],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="ticket-context">
      <Heading>Notat automat</Heading>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-[#6B7280]">{label}</dt>
            <dd className="text-[#373A45] font-mono text-xs break-all pt-0.5">{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

function Heading({ children, className = "" }) {
  return <h4 className={`text-xs text-[#6B7280] uppercase tracking-widest font-semibold mb-3 ${className}`}>{children}</h4>;
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-[#6B7280] uppercase tracking-wider">{label}</div>
      <div className="text-[#373A45] mt-0.5">{value || "—"}</div>
    </div>
  );
}
